// 📄 pages/Proyectos/BuscarFacturas.tsx
//
// "Buscar Facturas" (Commerce, 2026-09-07) — pantalla dedicada para el
// endpoint de "SAP Business Network Invoice Header Data Extraction" de
// Ariba (GET /invoices), mismo criterio que BuscarProveedores.tsx (SLP):
// reusa las credenciales de "Configuración > APIs" (tipo Ariba, OAuth2),
// muestra un aviso con link directo si el proyecto todavía no tiene una
// API así configurada.
//
// A diferencia de Buscar Proveedores, esta API pagina de verdad
// ($skip/$top, máx. 100 por página) en vez de devolver todo de una — la
// UI navega con Anterior/Siguiente usando firstPage/lastPage/totalPages
// que ya trae la respuesta de Ariba.
//
// Diseño de filtros (ajustado 2026-09-07 tras prueba real del usuario):
// Buyer ANID/Supplier ANID NO son campos prominentes — Ariba ignora el
// filtro buyerANID cuando quien llama es el comprador (que es el caso más
// común acá, ver docs oficiales), así que ponerlos como campo principal
// llevaba a búsquedas "vacías" por error. Lo que de verdad importa en el
// uso real es: N° de documento, rango de fechas (máx. 31 días) y estado —
// esos 3 son los campos prominentes; el resto queda en "Filtros avanzados".
// El rango de fechas es UN solo control con un selector de "por cuál
// fecha" (creación vs. actualización de estado) en vez de mostrar los 2
// pares de fechas de Ariba a la vez — más simple de usar, y sigue
// mandando el par correcto al backend según lo elegido.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, RefreshCw, AlertTriangle, KeyRound, Receipt, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useProject } from '../../context/ProjectContext.tsx';
import { obtenerConfigCliente, ApisCredenciales, ApiDefinition } from '../../services/clienteConexionService.ts';
import {
  buscarFacturas,
  FiltrosFacturas,
  FILTROS_FACTURAS_VACIOS,
  Factura,
  DOCUMENT_STATUS_OPCIONES,
  ROUTING_STATUS_OPCIONES,
  DOCUMENT_PURPOSE_OPCIONES
} from '../../services/comercioFacturasService.ts';
import FacturaCard from './components/FacturaCard.tsx';
import FacturaDetallePanel from './components/FacturaDetallePanel.tsx';
import LimiteUsoAlerta from '../../components/ui/LimiteUsoAlerta.tsx';

const TAMANOS_PAGINA = [10, 25, 50, 100];

type TipoFecha = 'creacion' | 'actualizacion';

// Ariba solo acepta 'YYYY-MM-DDTHH:mm:ss' — el input datetime-local del
// navegador entrega 'YYYY-MM-DDTHH:mm' (sin segundos), así que se
// completan acá antes de mandar la búsqueda.
const conSegundos = (valor: string): string => (valor && valor.length === 16 ? `${valor}:00` : valor);

const validarRangoFechas = (inicio: string, fin: string, nombreCampo: string): string | null => {
  if (!inicio && !fin) return null;
  if (!inicio || !fin) return `Para filtrar por ${nombreCampo} hay que indicar ambas fechas, o ninguna.`;
  const desde = new Date(inicio);
  const hasta = new Date(fin);
  if (Number.isNaN(desde.getTime()) || Number.isNaN(hasta.getTime())) return `Las fechas de ${nombreCampo} no son válidas.`;
  if (hasta.getTime() < desde.getTime()) return `El rango de ${nombreCampo} tiene la fecha de fin antes que la de inicio.`;
  const MAX_MS = 31 * 24 * 60 * 60 * 1000;
  if (hasta.getTime() - desde.getTime() > MAX_MS) return `El rango de ${nombreCampo} no puede superar 31 días (límite de Ariba).`;
  return null;
};

export default function BuscarFacturas() {
  const { projectId, project } = useProject();
  const clienteId = project?.clienteId || '';
  const { profile } = useAuth();
  // Cuota mensual de "API Tester", compartida con AribaApiTester.tsx y
  // BuscarProveedores.tsx (ver functions/services/monthlyQuota.js) — reset
  // perezoso, así que un contador de un mes viejo se muestra como 0.
  const mesActual = `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, '0')}`;
  const usoApiTesterVigente = profile?.usoApiTesterMes === mesActual ? (profile?.usoApiTesterCantidad ?? 0) : 0;

  const [apisAriba, setApisAriba] = useState<ApiDefinition[]>([]);
  const [cargandoApis, setCargandoApis] = useState(true);
  const [apiIdSeleccionada, setApiIdSeleccionada] = useState('');

  const [filtros, setFiltros] = useState<FiltrosFacturas>(FILTROS_FACTURAS_VACIOS);
  const [tipoFecha, setTipoFecha] = useState<TipoFecha>('creacion');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [avanzadosAbiertos, setAvanzadosAbiertos] = useState(false);
  const [pagina, setPagina] = useState(0);

  const [buscando, setBuscando] = useState(false);
  const [yaBusco, setYaBusco] = useState(false);
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [resultadoMeta, setResultadoMeta] = useState({ count: 0, totalPages: 1, firstPage: true, lastPage: true });
  const [errorBusqueda, setErrorBusqueda] = useState<string | null>(null);
  const [facturaAbierta, setFacturaAbierta] = useState<Factura | null>(null);

  useEffect(() => {
    if (!clienteId) { setCargandoApis(false); return; }
    let cancelado = false;
    setCargandoApis(true);
    obtenerConfigCliente<ApisCredenciales>(clienteId, 'apis')
      .then(({ apis }) => {
        if (cancelado) return;
        // Filtra por el path esperado, no solo por tipo — un proyecto puede
        // tener varias APIs Ariba configuradas para propósitos distintos
        // (esta misma + Supplier Data Pagination, Flow Extension, ...) y
        // mostrar todas en el selector obligaría a elegir a mano incluso
        // cuando hay una sola que realmente sirve para esta búsqueda.
        const ariba = (apis || []).filter((a) => a.tipo === 'ariba' && (a.api || '').toLowerCase().includes('invoice-extraction'));
        setApisAriba(ariba);
        if (ariba.length === 1) setApiIdSeleccionada(ariba[0].id);
      })
      .catch((err) => console.error('Error cargando las APIs Ariba configuradas:', err))
      .finally(() => { if (!cancelado) setCargandoApis(false); });
    return () => { cancelado = true; };
  }, [clienteId]);

  const actualizarFiltro = <K extends keyof FiltrosFacturas>(campo: K, valor: FiltrosFacturas[K]) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const errorFechas = useMemo(
    () => validarRangoFechas(fechaDesde, fechaHasta, tipoFecha === 'creacion' ? 'fecha de creación' : 'fecha de actualización de estado'),
    [fechaDesde, fechaHasta, tipoFecha]
  );

  const cantidadFiltrosAvanzados = useMemo(() => {
    return [filtros.buyerANID, filtros.supplierANID, filtros.routingStatus, filtros.documentPurpose, filtros.orderByField]
      .filter(Boolean).length;
  }, [filtros]);

  const ejecutarBusqueda = async (paginaDestino: number) => {
    if (!apiIdSeleccionada || !projectId || errorFechas) return;
    setBuscando(true);
    setYaBusco(true);
    setErrorBusqueda(null);
    try {
      const filtrosEnvio: FiltrosFacturas = {
        ...filtros,
        startDate: tipoFecha === 'creacion' ? conSegundos(fechaDesde) : '',
        endDate: tipoFecha === 'creacion' ? conSegundos(fechaHasta) : '',
        documentStatusUpdatedStartDate: tipoFecha === 'actualizacion' ? conSegundos(fechaDesde) : '',
        documentStatusUpdatedEndDate: tipoFecha === 'actualizacion' ? conSegundos(fechaHasta) : '',
        skip: paginaDestino * filtros.top
      };
      const resultado = await buscarFacturas(clienteId, projectId, apiIdSeleccionada, filtrosEnvio);
      if (!resultado.ok) {
        setErrorBusqueda(resultado.error || 'No se pudo completar la búsqueda.');
        setFacturas([]);
        setResultadoMeta({ count: 0, totalPages: 1, firstPage: true, lastPage: true });
      } else {
        setFacturas(resultado.facturas);
        setResultadoMeta({ count: resultado.count, totalPages: resultado.totalPages, firstPage: resultado.firstPage, lastPage: resultado.lastPage });
        setPagina(paginaDestino);
      }
    } catch (err: any) {
      setErrorBusqueda(err.message || 'No se pudo completar la búsqueda.');
      setFacturas([]);
      setResultadoMeta({ count: 0, totalPages: 1, firstPage: true, lastPage: true });
    } finally {
      setBuscando(false);
    }
  };

  const handleBuscar = () => ejecutarBusqueda(0);

  if (cargandoApis) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
        <p className="text-sm">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-light text-white flex items-center gap-2.5">
          <Receipt className="w-6 h-6 text-indigo-400" /> Buscar Facturas
        </h1>
        <p className="text-sm text-gray-400 mt-1">Consulta facturas de Commerce directo desde Ariba (Invoice Header Data Extraction) — estado, montos, direcciones y más.</p>
      </div>

      {apisAriba.length === 0 ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">Sin API de Ariba configurada</p>
            <p className="text-xs text-amber-200/80 mt-1">
              Esta búsqueda necesita una API tipo Ariba (OAuth2) en Configuración &gt; APIs, con el campo "API" en <code className="font-mono">/api/invoice-extraction/v1/prod</code>.
            </p>
            {projectId && (
              <Link to={`/proyecto/${projectId}/config`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 mt-2 transition-colors">
                Ir a Configuración &gt; APIs
              </Link>
            )}
          </div>
        </div>
      ) : (
        <>
          {apisAriba.length > 1 && (
            <div className="max-w-xs">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">API de Ariba a usar</label>
              <select
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                value={apiIdSeleccionada}
                onChange={(e) => setApiIdSeleccionada(e.target.value)}
              >
                <option value="">Elegí una API...</option>
                {apisAriba.map((a) => (
                  <option key={a.id} value={a.id}>{a.nombre || 'Sin nombre'}</option>
                ))}
              </select>
            </div>
          )}

          <LimiteUsoAlerta
            cantidad={usoApiTesterVigente}
            limite={profile?.limiteApiTesterMensual}
            etiqueta="ejecuciones de API Tester este mes"
          />

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">N° de documento</label>
              <input
                type="text"
                value={filtros.documentNumber}
                onChange={(e) => actualizarFiltro('documentNumber', e.target.value)}
                placeholder="Ej: F2447 (dejar vacío para buscar por rango)"
                className="w-full sm:max-w-xs bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr] gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Buscar por</label>
                <select
                  className="w-full sm:w-auto bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                  value={tipoFecha}
                  onChange={(e) => setTipoFecha(e.target.value as TipoFecha)}
                >
                  <option value="creacion">Fecha de creación</option>
                  <option value="actualizacion">Fecha de actualización de estado</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Desde</label>
                <input
                  type="datetime-local"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Hasta</label>
                <input
                  type="datetime-local"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
            <p className="text-[11px] text-gray-500 -mt-2">Hora en UTC. Máximo 31 días de rango. Si se deja vacío, Ariba devuelve los últimos 31 días.</p>
            {errorFechas && (
              <p className="text-xs text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {errorFechas}</p>
            )}

            <div className="max-w-xs">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Estado del documento</label>
              <select
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                value={filtros.documentStatus}
                onChange={(e) => actualizarFiltro('documentStatus', e.target.value)}
              >
                <option value="">Cualquiera</option>
                {DOCUMENT_STATUS_OPCIONES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <button
              type="button"
              onClick={() => setAvanzadosAbiertos((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200 transition-colors"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filtros avanzados{cantidadFiltrosAvanzados > 0 ? ` (${cantidadFiltrosAvanzados})` : ''}
              {avanzadosAbiertos ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {avanzadosAbiertos && (
              <div className="space-y-4 pt-2 border-t border-white/10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Buyer ANID</label>
                    <input
                      type="text"
                      value={filtros.buyerANID}
                      onChange={(e) => actualizarFiltro('buyerANID', e.target.value)}
                      placeholder="Rara vez hace falta: Ariba lo ignora si quien llama ya es el comprador"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Supplier ANID</label>
                    <input
                      type="text"
                      value={filtros.supplierANID}
                      onChange={(e) => actualizarFiltro('supplierANID', e.target.value)}
                      placeholder="Ej: AN11245161944-T"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Estado de enrutamiento</label>
                    <select
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                      value={filtros.routingStatus}
                      onChange={(e) => actualizarFiltro('routingStatus', e.target.value)}
                    >
                      <option value="">Cualquiera</option>
                      {ROUTING_STATUS_OPCIONES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Propósito</label>
                    <select
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                      value={filtros.documentPurpose}
                      onChange={(e) => actualizarFiltro('documentPurpose', e.target.value)}
                    >
                      <option value="">Cualquiera</option>
                      {DOCUMENT_PURPOSE_OPCIONES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-white/10">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Ordenar por</label>
                    <select
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                      value={filtros.orderByField}
                      onChange={(e) => actualizarFiltro('orderByField', e.target.value as FiltrosFacturas['orderByField'])}
                    >
                      <option value="">Fecha de creación (default)</option>
                      <option value="buyerANID">Buyer ANID</option>
                      <option value="supplierANID">Supplier ANID</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Dirección</label>
                    <select
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                      value={filtros.orderByDir}
                      onChange={(e) => actualizarFiltro('orderByDir', e.target.value as FiltrosFacturas['orderByDir'])}
                      disabled={!filtros.orderByField}
                    >
                      <option value="desc">Descendente</option>
                      <option value="asc">Ascendente</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Resultados por página</label>
                    <select
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                      value={filtros.top}
                      onChange={(e) => actualizarFiltro('top', Number(e.target.value))}
                    >
                      {TAMANOS_PAGINA.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtros.includeInvoiceProfile}
                    onChange={(e) => actualizarFiltro('includeInvoiceProfile', e.target.checked)}
                    className="accent-indigo-500"
                  />
                  Incluir datos de Invoice Profile
                </label>
              </div>
            )}

            <button
              type="button"
              onClick={handleBuscar}
              disabled={buscando || !apiIdSeleccionada || !!errorFechas}
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
            >
              {buscando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {buscando ? 'Buscando...' : 'Buscar facturas'}
            </button>
          </div>

          {errorBusqueda && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {errorBusqueda}
            </div>
          )}

          {yaBusco && !errorBusqueda && !buscando && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{resultadoMeta.count} factura{resultadoMeta.count !== 1 ? 's' : ''} encontrada{resultadoMeta.count !== 1 ? 's' : ''}</p>
                {resultadoMeta.totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => ejecutarBusqueda(pagina - 1)}
                      disabled={resultadoMeta.firstPage || buscando}
                      className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-gray-500">Página {pagina + 1} de {resultadoMeta.totalPages}</span>
                    <button
                      type="button"
                      onClick={() => ejecutarBusqueda(pagina + 1)}
                      disabled={resultadoMeta.lastPage || buscando}
                      className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              {facturas.length === 0 ? (
                <p className="text-sm text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                  Ninguna factura coincide con estos filtros.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {facturas.map((f, i) => (
                    <FacturaCard key={f.payloadId || f.documentNumber || i} factura={f} onVerDetalle={() => setFacturaAbierta(f)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {facturaAbierta && (
        <FacturaDetallePanel factura={facturaAbierta} onClose={() => setFacturaAbierta(null)} />
      )}
    </div>
  );
}
